/**
 * The work orders API (`/api/v1/work-orders`, LIAA service plan S2). Every write carries a version: the
 * order's for the header and status, the line's for a line (409 VERSION_CONFLICT on a stale one).
 */

import type {
  CancelReason,
  CreateInput,
  CustomerLookup,
  FeedItem,
  LineInput,
  LineState,
  ListParams,
  ListResponse,
  SavedView,
  StatusOption,
  UserLookup,
  VehicleLookup,
  WorkOrderCard,
} from '../types/workOrderTypes';

import { apiClient } from '@/shared/api/client';

const BASE = '/work-orders';

export async function fetchWorkOrders(params: ListParams): Promise<ListResponse> {
  const { urgent, archived, ...rest } = params;
  const { data } = await apiClient.get<ListResponse>(BASE, {
    params: {
      ...Object.fromEntries(Object.entries(rest).filter(([, v]) => v !== undefined && v !== '')),
      ...(urgent && { urgent: 'true' }),
      ...(archived && { archived: 'true' }),
    },
  });
  return data;
}

export async function fetchCounts(): Promise<{ open: number; mine: number; overdue: number }> {
  const { data } = await apiClient.get<{ counts: { open: number; mine: number; overdue: number } }>(`${BASE}/counts`);
  return data.counts;
}

export async function fetchWorkOrder(id: string): Promise<WorkOrderCard> {
  const { data } = await apiClient.get<{ workOrder: WorkOrderCard }>(`${BASE}/${id}`);
  return data.workOrder;
}

export async function createWorkOrder(input: CreateInput): Promise<WorkOrderCard> {
  const { data } = await apiClient.post<{ workOrder: WorkOrderCard }>(BASE, input);
  return data.workOrder;
}

type Card = Promise<WorkOrderCard>;
const card = async (p: Promise<{ data: { workOrder: WorkOrderCard } }>): Card => (await p).data.workOrder;

export function updateWorkOrder(
  id: string,
  input: { version: number; responsibleUserId?: string | null; urgent?: boolean; workTypeId?: string; payerCustomerId?: string },
): Card {
  return card(apiClient.patch(`${BASE}/${id}`, input));
}

export async function fetchStatusOptions(id: string): Promise<StatusOption[]> {
  const { data } = await apiClient.get<{ options: StatusOption[] }>(`${BASE}/${id}/status-options`);
  return data.options;
}

export function changeStatus(id: string, input: { version: number; statusId: string; comment: string | null }): Card {
  return card(apiClient.post(`${BASE}/${id}/status`, input));
}

export function cancelWorkOrder(id: string, input: { version: number; reason: CancelReason; comment: string | null }): Card {
  return card(apiClient.post(`${BASE}/${id}/cancel`, input));
}

/** "Mark as handed over" (S8.3) — an order whose car is not on an open service stay. */
export function markHandover(id: string, input: { version: number; at: string; to: string; comment: string | null }): Card {
  return card(apiClient.post(`${BASE}/${id}/handover`, input));
}

export function reopenWorkOrder(id: string, input: { version: number; reason: string }): Card {
  return card(apiClient.post(`${BASE}/${id}/reopen`, input));
}

export function archiveWorkOrder(id: string, input: { version: number; archived: boolean }): Card {
  return card(apiClient.post(`${BASE}/${id}/archive`, input));
}

export function addLine(id: string, input: LineInput): Card {
  return card(apiClient.post(`${BASE}/${id}/lines`, input));
}

export function updateLine(
  id: string,
  lineId: string,
  input: { version: number; description?: string; qty?: number; normMinutes?: number; unitPriceNet?: number; supplierName?: string | null; noCharge?: boolean; noChargeReason?: string | null },
): Card {
  return card(apiClient.patch(`${BASE}/${id}/lines/${lineId}`, input));
}

export function setLineState(id: string, lineId: string, input: { version: number; state: LineState }): Card {
  return card(apiClient.post(`${BASE}/${id}/lines/${lineId}/state`, input));
}

export function setLinePerformers(id: string, lineId: string, input: { version: number; staffIds: string[] }): Card {
  return card(apiClient.post(`${BASE}/${id}/lines/${lineId}/performers`, input));
}

export function removeLine(id: string, lineId: string, input: { version: number }): Card {
  return card(apiClient.post(`${BASE}/${id}/lines/${lineId}/remove`, input));
}

export async function fetchFeed(id: string): Promise<FeedItem[]> {
  const { data } = await apiClient.get<{ feed: FeedItem[] }>(`${BASE}/${id}/feed`);
  return data.feed;
}

export async function addComment(id: string, body: string): Promise<FeedItem[]> {
  const { data } = await apiClient.post<{ feed: FeedItem[] }>(`${BASE}/${id}/comments`, { body });
  return data.feed;
}

export async function lookupVehicles(q: string): Promise<VehicleLookup[]> {
  const { data } = await apiClient.get<{ vehicles: VehicleLookup[] }>(`${BASE}/lookups/vehicles`, { params: { q } });
  return data.vehicles;
}

export async function lookupCustomers(q: string): Promise<CustomerLookup[]> {
  const { data } = await apiClient.get<{ customers: CustomerLookup[] }>(`${BASE}/lookups/customers`, { params: { q } });
  return data.customers;
}

export async function lookupUsers(): Promise<UserLookup[]> {
  const { data } = await apiClient.get<{ users: UserLookup[] }>(`${BASE}/lookups/users`);
  return data.users;
}

export async function fetchViews(): Promise<SavedView[]> {
  const { data } = await apiClient.get<{ views: SavedView[] }>(`${BASE}/views`);
  return data.views;
}

export async function saveView(input: { name: string; shared: boolean; filters: SavedView['filters'] }): Promise<SavedView> {
  const { data } = await apiClient.post<{ view: SavedView }>(`${BASE}/views`, input);
  return data.view;
}

export async function deleteView(id: string): Promise<void> {
  await apiClient.post(`${BASE}/views/${id}/remove`, {});
}
